import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { uploadDirect } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Button, Empty, Input, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';

/** How a ticket's state reads to the customer. */
export const TICKET_STATUS = {
  open: { label: 'Open', tone: colors.info },
  in_processing: { label: 'Being worked on', tone: colors.warning },
  in_query: { label: 'Waiting on you', tone: colors.accent },
  resolved: { label: 'Resolved', tone: colors.success },
};

export const when = (value) => {
  const d = new Date(value);
  const mins = Math.round((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/**
 * Help desk: the customer's tickets, and the form to raise a new one.
 *
 * The questions come from admin rather than a hard-coded list, so support can
 * change what people can report without a release. `?refType=order&refId=…`
 * opens the form already pointed at that order.
 */
const SupportPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const refType = params.get('refType') || 'none';
  const refId = params.get('refId') || undefined;
  const refNo = params.get('refNo') || '';

  const [tab, setTab] = useState(refId ? 'new' : 'list');
  const [tickets, setTickets] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const [topic, setTopic] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, tp] = await Promise.all([
        api.support.tickets(),
        api.support.topics(refType === 'none' ? undefined : refType),
      ]);
      setTickets(t.data || []);
      setTopics(tp.data || []);
    } catch (e) {
      setTickets([]);
    } finally { setLoading(false); }
  }, [refType]);

  useEffect(() => { load(); }, [load]);

  const attach = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setProgress(0);
    try {
      // Straight to S3 - any format, any size, and the API never sees the bytes.
      const url = await uploadDirect(file, 'support', setProgress);
      setFiles((list) => [...list, {
        url, name: file.name, mime: file.type || '', size: file.size || 0,
      }]);
    } catch (err) {
      toast.error(err.message || 'Could not upload that file');
    } finally { setProgress(null); e.target.value = ''; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) return toast.error('Pick a question or write a short subject');
    setSaving(true);
    try {
      const res = await api.support.create({
        topicId: topic && topic._id,
        subject: subject.trim(),
        description: description.trim(),
        attachments: files,
        refType,
        refId,
      });
      navigate(`/support/${res.data._id}`);
    } catch (err) {
      toast.error(err.message || 'Could not raise the ticket');
      setSaving(false);
    }
    return undefined;
  };

  if (loading) return <Loading text="Loading your tickets…" />;

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader title="Help & support" subtitle="We usually reply within a few hours" />

      <div className="flex" style={{ gap: 8, marginBottom: 16 }}>
        <button type="button" className={`btn ${tab === 'list' ? 'primary' : 'ghost'} btn-sm`} onClick={() => setTab('list')}>
          My tickets
        </button>
        <button type="button" className={`btn ${tab === 'new' ? 'primary' : 'ghost'} btn-sm`} onClick={() => setTab('new')}>
          Raise a ticket
        </button>
      </div>

      {tab === 'list' ? (
        !tickets.length ? (
          <Empty
            emoji="🎧"
            title="No tickets yet"
            text="Raise one and our team will reply right here."
            actionLabel="Raise a ticket"
            onAction={() => setTab('new')}
          />
        ) : tickets.map((t, i) => {
          const s = TICKET_STATUS[t.status] || TICKET_STATUS.open;
          return (
            <Reveal key={t._id} delay={stagger(i, 40)}>
              <button
                type="button"
                onClick={() => navigate(`/support/${t._id}`)}
                className="card sh-sm"
                style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 12 }}
              >
                <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                  <span className="t-body grow" style={{ fontWeight: 700 }}>{t.subject}</span>
                  <span className="t-tiny" style={{ color: s.tone, fontWeight: 800 }}>{s.label}</span>
                </div>
                <div className="t-tiny">
                  {t.ticketNo}{t.refNo ? ` · about ${t.refNo}` : ''} · {when(t.lastMessageAt)}
                  {t.unreadForUser ? (
                    <strong style={{ color: colors.primary }}> · {t.unreadForUser} new</strong>
                  ) : null}
                </div>
              </button>
            </Reveal>
          );
        })
      ) : (
        <form className="card sh-sm" onSubmit={submit}>
          {refNo ? (
            <div className="t-tiny" style={{ marginBottom: 10 }}>About <strong>{refNo}</strong></div>
          ) : null}

          <div className="t-tiny" style={{ letterSpacing: 1, marginBottom: 8 }}>WHAT IS THIS ABOUT?</div>
          <div className="col" style={{ gap: 8, marginBottom: 16 }}>
            {topics.map((t) => {
              const active = topic && topic._id === t._id;
              return (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => { setTopic(t); setSubject(t.title); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                    background: active ? colors.primaryLight : 'var(--card, #fff)',
                    border: `2px solid ${active ? colors.primary : colors.line}`,
                    borderRadius: 12, padding: 12,
                  }}
                >
                  <span style={{ fontSize: 20 }}>{t.emoji || '💬'}</span>
                  <span className="grow">
                    <span className="t-body" style={{ fontWeight: 700, display: 'block' }}>{t.title}</span>
                    {t.hint ? <span className="t-tiny">{t.hint}</span> : null}
                  </span>
                </button>
              );
            })}
            {!topics.length ? (
              <div className="t-tiny">
                No topics are set up yet - describe the problem below and we will pick it up.
              </div>
            ) : null}
          </div>

          <Input
            label="Subject"
            icon="📝"
            placeholder="A one line summary"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <label className="t-tiny" style={{ letterSpacing: 1, display: 'block', margin: '12px 0 6px' }}>
            TELL US MORE
          </label>
          <textarea
            className="input"
            rows={5}
            maxLength={4000}
            placeholder="What happened? Anything that helps us fix it faster."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ resize: 'vertical' }}
          />

          <div className="t-tiny" style={{ letterSpacing: 1, margin: '16px 0 6px' }}>
            ATTACHMENTS — ANY FILE, ANY SIZE
          </div>
          {files.map((f, i) => (
            <div key={f.url} className="flex" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <span>📎</span>
              <span className="t-small grow">{f.name}</span>
              <button
                type="button"
                className="btn ghost btn-sm"
                onClick={() => setFiles((list) => list.filter((_, n) => n !== i))}
              >
                Remove
              </button>
            </div>
          ))}
          <label className="btn ghost" style={{ cursor: 'pointer' }}>
            {progress !== null ? `Uploading ${Math.round(progress * 100)}%` : '➕ Add a file'}
            <input type="file" hidden onChange={attach} />
          </label>

          <Button
            title="Submit ticket"
            icon="🎫"
            full
            loading={saving}
            disabled={saving || progress !== null}
            style={{ marginTop: 16 }}
            onClick={submit}
          />
        </form>
      )}
    </div>
  );
};

export default SupportPage;
